import { useEffect } from 'react';
import type { TradeForm } from '../../components/trade-form-sections/types';

type TradeFormField = Parameters<TradeForm['validateField']>[0];

/**
 * LR-718: clear a blocking error left on a field this trade no longer uses.
 *
 * A conditionally rendered `<form.Field>` is never unregistered: `FieldApi.mount()`
 * returns an empty cleanup, so `fieldInfo[name].instance` and its meta both survive
 * the unmount, and `isFieldsValid` keeps counting them. Gating each validator on the
 * live form state (the fix this hook accompanies) stops a HIDDEN field from arming a
 * fresh error, but it cannot retract one that was armed while the field was still on
 * screen — and that one is unreachable: `_handleSubmit` bails on `!canSubmit` BEFORE
 * `validateAllFields`, so the gated validator never gets to run and say "no error".
 *
 * Re-running it by hand on the way out is what breaks the deadlock. The trader's only
 * other way out is to close and reopen the modal (`form.reset()`), which loses the
 * whole form.
 *
 * Only fields that actually carry an error are touched: `FormApi.validateField` sets
 * `isTouched` on the way in, and there is no reason to mark clean fields on mount.
 *
 * Pass `fields` as a module-level constant — it is a dependency.
 */
export function useHiddenFieldErrorThaw({
  form,
  applies,
  fields,
}: {
  form: TradeForm;
  /**
   * Does the trade's SHAPE still use these fields? NOT "are they on screen": a
   * collapsed section unmounts its fields too, and passing that in would be
   * pointless rather than harmful - this hook re-runs the field's own gated
   * validator rather than clearing meta, so an error the shape still justifies
   * is simply re-armed and the button stays dead with nothing new on screen.
   * Collapse is handled where it belongs, by reopening the section on a refused
   * Create (`onSubmitInvalid`, useCreateTradeForm).
   */
  applies: boolean;
  fields: readonly TradeFormField[];
}) {
  useEffect(() => {
    if (applies) return;
    for (const name of fields) {
      // `void`: an async validator added later would return a promise here.
      if (form.state.fieldMeta[name]?.errors.length) void form.validateField(name, 'change');
    }
  }, [applies, fields, form]);
}
